import { useState } from "react"

import { FormProvider } from "react-hook-form"
import { useTranslation } from "react-i18next"
import { useToggle } from "react-use"

import Heading from "$/components/Heading/Heading"
import { getErrorMessages } from "$/utils/get-error-messages"

import {
    Button,
    ETypographyAlignment,
    ETypographyFamily,
    ETypographySize,
    ETypographyTag,
    ETypographyWeight,
    Typography
} from "@/3514/components"
import { TUserState, useUser } from "@/3514/hooks"
import { EUserRole } from "@/3514/types"
// WISER-5808
import "@/assets/coach.scss"
import { CalendarAccounts, CalendarPrimary, CalendarSyncAvailability } from "@/entities/coach-calendar"
import { useCoachCalendarOrderNavigation } from "@/entities/coach-calendar/hooks"
import { useCalendarLinkSetting } from "@/entities/coach-calendar/hooks/use-calendar-link-setting"
import { SimpleLayout } from "@/layouts"
import DateWidget from "@/shared/DateWidget"
// WISER-5808
import { WarningMessage } from "@/shared/WarningMessage"

import { CoachCalendarStepUrls } from "./constants"
import { StepsNavigator } from "./ui/StepNavigator"

type Props = {
    order?: number
}

const LinkCalendarStep = ({ order }: Props) => {
    const { user }: TUserState<EUserRole.Coach> = useUser<EUserRole.Coach>()

    const { t } = useTranslation()
    const { navigateByOrder } = useCoachCalendarOrderNavigation(order)
    const {
        api,
        onSubmit,
        hookForm: methods
    } = useCalendarLinkSetting({ isQueryEnabled: !user.isCalendarSetupConfirmed })

    const [error, setError] = useState(null)

    const [isSubmitButtonLoading, setIsSubmitButtonLoading]: ReturnType<typeof useToggle> = useToggle(false)

    const onNextClick = async values => {
        setIsSubmitButtonLoading(true)

        try {
            await onSubmit(values)

            navigateByOrder()
        } catch (e) {
            setError(getErrorMessages(e))
        } finally {
            setIsSubmitButtonLoading(false)
        }
    }

    const hasLinkedAccount = api?.data?.length > 0

    const errorMsg = [
        methods?.formState?.errors?.calendars?.message,
        methods?.formState?.errors?.primaryCalendar?.message
    ]
        .filter(Boolean)
        .join(", ")

    return (
        <SimpleLayout
            header={<DateWidget className="coach-date-title" />}
            headerVariant="gray"
            showProfilePhoto
            showBackBtn
            withUserDropdownWithoutMargin
            className="coach-link-calendar-layout"
        >
            <div className="coach-calendar-wrapper mx-auto pb-5 text-center">
                <div className="d-flex justify-content-center mt-5">
                    <StepsNavigator activeStep={CoachCalendarStepUrls.LINK_CALENDAR_STEP} />
                </div>
                <Heading textAlign="center" tag="h6" fontSize={16} className="mb-[30px] mt-5 text-accentOld">
                    Step {order}
                </Heading>
                <Typography
                    text="Link Your Calendar(s)"
                    className="mb-2"
                    alignment={ETypographyAlignment.Center}
                    tag={ETypographyTag.Heading1}
                    weight={ETypographyWeight.Medium}
                    size={ETypographySize.Heading1}
                    family={ETypographyFamily.DmSerif}
                />
                <p className="mb-1 color-gray fs-14">
                    Choose which calendar(s) you’d like to sync to Wiser to inform your availability.
                </p>
                <p className="color-gray fs-14 mb-[20px]">
                    You will be able to manually tweak your availability later as needed.
                </p>
                <CalendarAccounts isQueryEnabled={!user.isCalendarSetupConfirmed} throwOnError={false} />

                {hasLinkedAccount && (
                    <>
                        <Heading tag="h4" textAlign="center" className="mb-2 mt-[60px]" fontSize={16}>
                            Sync Availability with Sub-calendars
                        </Heading>
                        <p className="color-gray fs-14 mb-4 text-center">
                            Each of your calendars likely has sub-calendars which you may or may not want to use to
                            inform your availability. Common examples of sub-calendars include: work, personal, and
                            national holidays. Choose which sub-calendars to sync below.
                        </p>
                        <FormProvider {...methods}>
                            <CalendarSyncAvailability data={api?.data} />

                            <Heading tag="h4" textAlign="center" className="mb-2 mt-[60px]" fontSize={16}>
                                {t("coachSide.linkCalendar.selectCalendarBlock.title")}
                            </Heading>
                            <p className="color-gray fs-14 mb-0 text-center">
                                {t("coachSide.linkCalendar.selectCalendarBlock.description.p1")}
                            </p>
                            <p className="color-gray fs-14 mb-4 text-center">
                                {t("coachSide.linkCalendar.selectCalendarBlock.description.p2")}
                            </p>
                            <CalendarPrimary data={api?.data} />
                        </FormProvider>
                        <WarningMessage
                            message={error || errorMsg || getErrorMessages(api?.error?.response?.data)?.join("")}
                        />
                        <div className="w-min mx-auto mt-[60px]">
                            <Button
                                className="!bg-accentOld"
                                isBusy={isSubmitButtonLoading}
                                onClick={methods.handleSubmit(onNextClick)}
                            >
                                Next
                            </Button>
                        </div>
                    </>
                )}
            </div>
        </SimpleLayout>
    )
}

export default LinkCalendarStep
